import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Users, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useRoleUsage } from '../../shared/lib/hooks/use-roles';
import type { Role, User } from '@/shared/types';

interface RoleDeleteDialogProps {
  role: Role | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
  isDeleting?: boolean;
}

export const RoleDeleteDialog: React.FC<RoleDeleteDialogProps> = ({
  role,
  open,
  onOpenChange,
  onConfirm,
  isDeleting = false,
}) => {
  const { t } = useTranslation();
  
  // Check role usage when dialog is open and role is selected
  const { 
    data: usageInfo, 
    isLoading: isCheckingUsage,
    error: usageError 
  } = useRoleUsage(role?.id || '', open && !!role);

  const handleConfirm = () => {
    if (!usageInfo?.isInUse) {
      onConfirm();
    }
  };

  const canDelete = usageInfo && !usageInfo.isInUse;
  const isInUse = usageInfo?.isInUse;
  const assignedUsers = usageInfo?.assignedUsers || [];
  const hasUnknownUserCount = usageInfo?.assignedUsersCount === -1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            {t('roles.deleteRoleTitle')}
          </DialogTitle>
          <DialogDescription>
            {role && (
              <span className="font-medium">
                {role.name}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Loading state */}
          {isCheckingUsage && (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-6 w-6 animate-spin mr-2" />
              <span className="text-sm text-muted-foreground">
                {t('roles.checkingRoleUsage')}
              </span>
            </div>
          )}

          {/* Error state */}
          {!!usageError && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {t('errors.generic', { message: 'Failed to check role usage' })}
              </AlertDescription>
            </Alert>
          )}

          {/* Role can be deleted */}
          {canDelete && (
            <div className="space-y-3">
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  {t('roles.deleteRoleDescription')}
                </AlertDescription>
              </Alert>
            </div>
          )}

          {/* Role is in use */}
          {isInUse && (
            <div className="space-y-3">
              <Alert variant="destructive">
                <Users className="h-4 w-4" />
                <AlertDescription>
                  {t('roles.deleteRoleInUseDescription')}
                </AlertDescription>
              </Alert>

              {(assignedUsers.length > 0 || hasUnknownUserCount) && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Users className="h-4 w-4" />
                    {hasUnknownUserCount 
                      ? t('roles.assignedUsers')
                      : `${t('roles.assignedUsers')} (${assignedUsers.length})`
                    }
                  </div>
                  
                  {hasUnknownUserCount ? (
                    <div className="p-4 rounded-md bg-muted/50 text-center">
                      <span className="text-sm text-muted-foreground">
                        {t('roles.cannotDetermineUsers')}
                      </span>
                    </div>
                  ) : (
                    <ScrollArea className="h-32 w-full rounded-md border p-2">
                      <div className="space-y-2">
                        {assignedUsers.map((user: User) => (
                          <div
                            key={user.id}
                            className="flex items-center justify-between p-2 rounded-md bg-muted/50"
                          >
                            <div className="flex flex-col">
                              <span className="text-sm font-medium">
                                {user.firstName && user.lastName 
                                  ? `${user.firstName} ${user.lastName}`
                                  : user.email
                                }
                              </span>
                              {user.firstName && user.lastName && (
                                <span className="text-xs text-muted-foreground">
                                  {user.email}
                                </span>
                              )}
                            </div>
                            <Badge variant={user.isActive ? 'default' : 'secondary'}>
                              {user.isActive ? t('common.active') : t('common.inactive')}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isDeleting}
          >
            {t('common.cancel')}
          </Button>
          
          {canDelete ? (
            <Button
              variant="destructive"
              onClick={handleConfirm}
              disabled={isDeleting || isCheckingUsage}
            >
              {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('common.delete')}
            </Button>
          ) : (
            <Button
              variant="outline"
              disabled
              className="text-muted-foreground"
            >
              {t('roles.cannotDeleteRoleInUse')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};